/**
 * Staging style packs.
 *
 * ONE prop kit, three tints. The decision behind that shape is the user's own
 * observation that open houses all show the same things — a plant, books,
 * minimal decoration — which the staging literature agrees with: neutrals
 * dominate and colour enters only at the ~10% accent layer. So a pack does not
 * change WHICH props are placed, only their palette, their art motif and the
 * tone of their materials. Three prop vocabularies would be three times the
 * assets for a difference buyers do not read.
 *
 * KITCHENS are the exception, and a deliberate one: a pack also picks what a
 * kitchen prop IS — lemons, apples or pears in the bowl; an oak, stone or
 * walnut tray; turned-wood or acrylic mills; penne, spaghetti or fusilli in
 * the jar. Megan chose these per style over 11 review rounds (2026-10-18). The
 * choice lives in each pack's `kitchen` block; the planner stamps the style id
 * on a kitchen prop as `attributes.variant` and the renderer looks the block
 * up here, so planner and drawing read one table.
 *
 * Shape and discipline mirror `kitchenStylePresets` / `dormThemePresets`: a
 * plain table of pure data, resolved through a deep clone so a caller can
 * never mutate the shared record, and an unknown id throws rather than
 * silently falling back.
 */

const STAGING_STYLES = {
  // The safest staging default: "neutral plus warmth", and the style the
  // research names as the middle ground for resale.
  'warm-neutral': {
    id: 'warm-neutral',
    label: 'Warm neutral',
    palette: ['#EFE7D6', '#C2A98A', '#B98A4E', '#6C7A5C', '#7E7466'],
    accent: '#B98A4E',
    art: {
      motif: 'minimal-arch',
      colors: ['#EFE7D6', '#B98A4E', '#6C7A5C'],
      seed: 7,
    },
    towel: '#F3EEE2',
    kitchen: {
      // Kitchen art: a kitchen SUBJECT in this pack's own art palette, so the
      // house reads as one set by colour and frame (Megan's pick, 2026-10-21).
      art: {
        motif: 'kitchen-citrus-branch',
        colors: ['#EFE7D6', '#B98A4E', '#6C7A5C'],
        seed: 7,
      },
      fruit: 'lemon',
      vase: '#E4DACA',
      flower: '#F7F2E8',
      trayKind: 'rect',
      tray: '#C49A64',
      mill: 'turned',
      millWood: '#5A3A22',
      pasta: 'penne',
      crock: '#EDE6D8',
      canLid: '#B98A4E',
      cookbookCover: '#6C7A5C',
      teaTowel: '#B98A4E',
      teaTowelStripe: '#F3EEE2',
    },
    // Bedroom soft goods. White duvet, oatmeal euros, the accent at the 10%
    // layer only (lumbar + throw) -- "neutrals dominate; colour enters at the
    // accent layer" (research doc 1.3). Lamp base is the pack's ceramic.
    bedding: {
      duvet: '#F7F4EE',
      euro: '#E3D7C3',
      sham: '#FBF9F4',
      lumbar: '#B98A4E',
      // The square accents carry the bed's second pattern (the throw is the
      // first); two patterns is the most a staged bed takes.
      accentPillow: '#EFE7D6',
      accentPattern: { pattern: 'stripe', colors: ['#EFE7D6', '#B98A4E'] },
      // A textured duvet reads as cloth in white; a smooth one reads as a slab.
      duvetTexture: 'seersucker',
      throw: '#C2A98A',
      // The foot-of-bed throw is the bed's one patterned piece.
      throwPattern: {
        pattern: 'plaid',
        colors: ['#E6DCC8', '#B98A4E', '#7E7466'],
      },
      lampBase: '#EDE6D8',
      lampShade: '#F2ECE0',
      vase: '#9C8C78',
      rug: ['#E9E1D2', '#C2A98A'],
      planter: '#E8E2D6',
      // The bed's headboard: oatmeal linen panel (Pottery Barn Clara / Raleigh).
      headboard: { kind: 'panel', color: '#D8CFC0' },
    },
  },
  'modern-minimal': {
    id: 'modern-minimal',
    label: 'Modern minimal',
    palette: ['#F4F4F2', '#DDDDD8', '#9A9A95', '#4A4A47', '#2B2B29'],
    accent: '#9A9A95',
    art: {
      motif: 'neutral-color-block',
      colors: ['#F4F4F2', '#9A9A95', '#2B2B29'],
      seed: 11,
    },
    towel: '#FBFBFA',
    kitchen: {
      // Kitchen art: a kitchen SUBJECT in this pack's own art palette, so the
      // house reads as one set by colour and frame (Megan's pick, 2026-10-21).
      art: {
        motif: 'kitchen-citrus-slice',
        colors: ['#F4F4F2', '#9A9A95', '#2B2B29'],
        seed: 11,
      },
      fruit: 'apple',
      vase: '#3A3A37',
      flower: '#FAFAF7',
      trayKind: 'round',
      tray: '#3B3B38',
      mill: 'acrylic',
      millMetal: '#BFC2C4',
      pasta: 'spaghetti',
      crock: '#F4F4F2',
      canLid: '#2B2B29',
      cookbookCover: '#2B2B29',
      teaTowel: '#7C7C77',
      teaTowelStripe: '#FBFBFA',
    },
    bedding: {
      duvet: '#FAFAF8',
      euro: '#DDDDD8',
      sham: '#FFFFFF',
      lumbar: '#4A4A47',
      // No print: minimalism carries depth in tone and texture (the waffle
      // duvet), not pattern. Stripes here read as a prison cell (review).
      accentPillow: '#B9B6AE',
      accentPattern: null,
      duvetTexture: 'waffle',
      throw: '#9A9A95',
      throwPattern: null,
      lampBase: '#2B2B29',
      lampShade: '#F4F4F2',
      vase: '#F4F4F2',
      rug: ['#ECECE8', '#9A9A95'],
      planter: '#3A3A38',
      // The bed's headboard: plain channels, tone on tone (Crate & Barrel Tate).
      headboard: { kind: 'channel', color: '#BDBAB3' },
    },
  },
  'warm-organic': {
    id: 'warm-organic',
    label: 'Warm organic',
    palette: ['#EDE4D3', '#CBB79A', '#A9754F', '#7E8B6B', '#5B5345'],
    accent: '#A9754F',
    art: {
      motif: 'botanical-plate',
      colors: ['#EDE4D3', '#A9754F', '#7E8B6B'],
      seed: 23,
    },
    towel: '#F2EADC',
    kitchen: {
      // Kitchen art: a kitchen SUBJECT in this pack's own art palette, so the
      // house reads as one set by colour and frame (Megan's pick, 2026-10-21).
      art: {
        motif: 'kitchen-herb-botanical',
        colors: ['#EDE4D3', '#A9754F', '#7E8B6B'],
        seed: 23,
      },
      fruit: 'pear',
      vase: '#CBB79A',
      flower: '#EFD9C6',
      trayKind: 'oval',
      tray: '#7A4E2E',
      mill: 'turned',
      millWood: '#A57B4F',
      pasta: 'fusilli',
      crock: '#D9CCB6',
      canLid: '#7E8B6B',
      cookbookCover: '#7E8B6B',
      teaTowel: '#A9754F',
      teaTowelStripe: '#F2EADC',
    },
    bedding: {
      duvet: '#F4EDE1',
      euro: '#CBB79A',
      sham: '#EDE4D3',
      lumbar: '#A9754F',
      accentPillow: '#EDE4D3',
      accentPattern: { pattern: 'gingham', colors: ['#EDE4D3', '#7E8B6B'] },
      duvetTexture: 'waffle',
      throw: '#7E8B6B',
      // A tonal sage herringbone with a knotted fringe (Birch Lane Dacion):
      // a woven texture, not a print. The small floral read as a kid's
      // blanket at listing-photo distance (review, 2026-10-27).
      throwPattern: { pattern: 'herringbone', colors: ['#7E8B6B', '#A3AD90'] },
      lampBase: '#B07A55',
      lampShade: '#EDE4D3',
      vase: '#E6DCC8',
      rug: ['#E6DCC8', '#A9754F'],
      planter: '#9C6B4A',
      // The bed's headboard: oak with dowelled stiles (West Elm Mid-Century).
      headboard: { kind: 'wood', color: '#B08A62' },
    },
  },
};

const STAGING_STYLE_ORDER = Object.keys(STAGING_STYLES);
const DEFAULT_STAGING_STYLE_ID = 'warm-neutral';

function resolveStagingStyle(styleId) {
  const id = styleId || DEFAULT_STAGING_STYLE_ID;
  const found = STAGING_STYLES[id];
  if (!found) {
    throw new Error(
      `resolveStagingStyle: unknown style "${id}". Known: ${STAGING_STYLE_ORDER.join(', ')}`
    );
  }
  // Deep clone so a planner can never write through to the shared table.
  return JSON.parse(JSON.stringify(found));
}

/**
 * A style's kitchen look, by style id. Renderers receive the id as
 * `options.variant`; an unknown or missing id draws the default style rather
 * than nothing, because a persisted prop must always render.
 */
function kitchenLookFor(styleId) {
  const pack =
    STAGING_STYLES[styleId] || STAGING_STYLES[DEFAULT_STAGING_STYLE_ID];
  return pack.kitchen;
}

module.exports = {
  kitchenLookFor,
  STAGING_STYLES,
  STAGING_STYLE_ORDER,
  DEFAULT_STAGING_STYLE_ID,
  resolveStagingStyle,
};
